import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import commonActions from '../../../Actions/commonActions';
import priceSimulationActions from '../../../Actions/priceSimulationActions';
import useModal from '../../../Hooks/useModal';
import DateTimePicker from '../../DateTimePicker';
import ComboBox from '../../Fundamentals/ComboBox/ComboBox';
import { BaseModal, ModalBody, ModalFooter, ModalHeader, ModalHeaderTitle, ModalSize } from '../../Fundamentals/Modal/StandardModal';
import Spinner from '../../Fundamentals/Spinner/Spinner';
import { extractDraftModifications } from './proposalPayloadExtractor';

import './SendPriceForApprovalModalBeta.css';

const DRAFT_STATUS = 0;

const formatPrice = (value) =>
  new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    value
  );

const IndeterminateCheckbox = ({ checked, indeterminate, onChange }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} />;
};

const SendPriceForApprovalModalBeta = ({ adjustments = {}, selectedPackageIds = [] }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { closeModal } = useModal();

  const packageDetailsMap = useSelector((state) => state.PriceSimulationReducer.packageDetailsMap);
  const reviewers = useSelector((state) => state.PriceSimulationReducer.reviewers);
  const isSubmitting = useSelector(
    (state) => state.CommonReducer.dataLoading.submitApproval === true
  );

  const [selectedProductKeys, setSelectedProductKeys] = useState(() => new Set());
  const [selectedReviewerIds, setSelectedReviewerIds] = useState([]);
  const [notes, setNotes] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [expandedPackages, setExpandedPackages] = useState(() => new Set());
  const [expandedTiers, setExpandedTiers] = useState(() => new Set());

  useEffect(() => {
    dispatch(priceSimulationActions.fetchReviewers());
  }, []);

  const draftPackageGroups = useMemo(() => {
    const groups = [];

    for (const packageId of selectedPackageIds) {
      const pkg = packageDetailsMap[packageId];
      if (!pkg?.tiers) continue;

      const tiers = [];
      pkg.tiers.forEach((tier) => {
        const tierId = tier.tierId || tier.id;
        const tierKey = `${packageId}-${tierId}`;
        const products = [];

        tier.productGroups.forEach((group) => {
          const groupId = group.groupId || group.id;
          const groupName = group.name;

          group.products.forEach((product) => {
            const productId = product.productId || product.id;
            const productName = product.name;

            Object.entries(product.prices || {}).forEach(([planIdentifier, pricePoint]) => {
              const isSavedDraft =
                pricePoint.isModified === true &&
                (pricePoint.approvalStatus == null || pricePoint.approvalStatus === DRAFT_STATUS);

              const compositeProductId = `${packageId}-${tierId}-${groupId}-${productId}`;
              const compositePlanId = `${packageId}-${tierId}-${planIdentifier}-${productId}`;
              const adj = adjustments[compositeProductId]?.[compositePlanId];
              const isUnsaved = adj && (adj.percentage !== 0 || adj.newPrice != null);

              const isDraftOrNew =
                pricePoint.approvalStatus == null || pricePoint.approvalStatus === DRAFT_STATUS;
              if (isSavedDraft || (isUnsaved && isDraftOrNew)) {
                const currentPrice = pricePoint.currentPrice ?? 0;
                const newPrice =
                  isUnsaved && adj.newPrice != null
                    ? adj.newPrice
                    : (pricePoint.newPrice ?? currentPrice);
                const adjustmentPct = isUnsaved
                  ? (adj.percentage ?? 0)
                  : (pricePoint.adjustmentPercentage ?? 0);

                products.push({
                  productKey: `${packageId}-${tierId}-${productId}-${planIdentifier}`,
                  productId,
                  productName,
                  groupName,
                  planIdentifier,
                  currentPrice,
                  newPrice,
                  adjustmentPct,
                  tierId,
                  groupId,
                  pricingScheme: pricePoint.pricingScheme ?? 0,
                  tieredPrices: pricePoint.tieredPrices ?? [],
                });
              }
            });
          });
        });

        if (products.length > 0) {
          tiers.push({ tierKey, tierId, tierName: tier.name, products });
        }
      });

      if (tiers.length > 0) {
        groups.push({ packageId, packageName: pkg.packageName, tiers });
      }
    }

    return groups;
  }, [packageDetailsMap, selectedPackageIds, adjustments]);

  const allDraftProducts = useMemo(
    () => draftPackageGroups.flatMap((g) => g.tiers.flatMap((tierItem) => tierItem.products)),
    [draftPackageGroups]
  );

  useEffect(() => {
    if (allDraftProducts.length > 0 && selectedProductKeys.size === 0) {
      setSelectedProductKeys(new Set(allDraftProducts.map((p) => p.productKey)));
      setExpandedPackages(new Set(draftPackageGroups.map((g) => g.packageId)));
      setExpandedTiers(
        new Set(draftPackageGroups.flatMap((g) => g.tiers.map((tierItem) => tierItem.tierKey)))
      );
    }
  }, [allDraftProducts]);

  const allSelected =
    allDraftProducts.length > 0 && selectedProductKeys.size === allDraftProducts.length;

  const handleSelectAll = useCallback(() => {
    setSelectedProductKeys(
      allSelected ? new Set() : new Set(allDraftProducts.map((p) => p.productKey))
    );
  }, [allSelected, allDraftProducts]);

  const handleToggleProduct = useCallback((productKey) => {
    setSelectedProductKeys((prev) => {
      const next = new Set(prev);
      next.has(productKey) ? next.delete(productKey) : next.add(productKey);
      return next;
    });
  }, []);

  const handleToggleTierSelection = useCallback((tierProducts) => {
    setSelectedProductKeys((prev) => {
      const keys = tierProducts.map((p) => p.productKey);
      const allIn = keys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allIn) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
      }
      return next;
    });
  }, []);

  const handleTogglePackageSelection = useCallback((packageTiers) => {
    setSelectedProductKeys((prev) => {
      const keys = packageTiers.flatMap((tierItem) => tierItem.products.map((p) => p.productKey));
      const allIn = keys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allIn) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
      }
      return next;
    });
  }, []);

  const handleTogglePackageExpand = useCallback((packageId) => {
    setExpandedPackages((prev) => {
      const next = new Set(prev);
      next.has(packageId) ? next.delete(packageId) : next.add(packageId);
      return next;
    });
  }, []);

  const handleToggleTierExpand = useCallback((tierKey) => {
    setExpandedTiers((prev) => {
      const next = new Set(prev);
      next.has(tierKey) ? next.delete(tierKey) : next.add(tierKey);
      return next;
    });
  }, []);

  const selectedCount = selectedProductKeys.size;

  const summaryStats = useMemo(() => {
    let totalProducts = 0;
    let totalRevenueImpact = 0;

    allDraftProducts
      .filter((p) => selectedProductKeys.has(p.productKey))
      .forEach((p) => {
        totalProducts++;
        totalRevenueImpact += (p.newPrice ?? 0) - (p.currentPrice ?? 0);
      });

    return { totalProducts, totalRevenueImpact };
  }, [selectedProductKeys, allDraftProducts]);

  const canSubmit = selectedCount > 0 && selectedReviewerIds.length > 0 && !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;

    const unsavedPayloads = extractDraftModifications(
      packageDetailsMap,
      selectedPackageIds,
      adjustments
    );
    const packages = [];

    for (const packageId of selectedPackageIds) {
      const pkg = packageDetailsMap[packageId];
      if (!pkg?.tiers) continue;

      const modifications = [];

      pkg.tiers.forEach((tier) => {
        const tierId = tier.tierId || tier.id;

        tier.productGroups.forEach((group) => {
          group.products.forEach((product) => {
            const productId = product.productId || product.id;
            Object.entries(product.prices || {}).forEach(([planIdentifier, pricePoint]) => {
              const productKey = `${packageId}-${tierId}-${productId}-${planIdentifier}`;
              if (!selectedProductKeys.has(productKey)) return;

              if (
                pricePoint.isModified === true &&
                (pricePoint.approvalStatus == null || pricePoint.approvalStatus === DRAFT_STATUS)
              ) {
                const isTiered = (pricePoint.pricingScheme ?? 0) === 1;
                modifications.push({
                  TierId: tierId,
                  ProductId: productId,
                  PlanIdentifier: planIdentifier,
                  PricingScheme: pricePoint.pricingScheme ?? 0,
                  AdjustmentPercentage: pricePoint.adjustmentPercentage ?? 0,
                  NewPrice: isTiered ? null : (pricePoint.newPrice ?? pricePoint.currentPrice ?? 0),
                  BasePriceAtCreation: pricePoint.currentPrice ?? 0,
                  TieredPrices: isTiered ? (pricePoint.tieredPrices ?? []) : [],
                  Status: 0,
                });
              }
            });
          });
        });
      });

      const unsavedPkg = unsavedPayloads.find((p) => p.packageId === packageId);
      if (unsavedPkg) {
        const unsavedKeys = new Set(
          unsavedPkg.modifications.map((m) => `${m.TierId}-${m.ProductId}-${m.PlanIdentifier}`)
        );
        const filtered = modifications.filter(
          (m) => !unsavedKeys.has(`${m.TierId}-${m.ProductId}-${m.PlanIdentifier}`)
        );
        const selectedUnsaved = unsavedPkg.modifications.filter((m) => {
          const productKey = `${packageId}-${m.TierId}-${m.ProductId}-${m.PlanIdentifier}`;
          return selectedProductKeys.has(productKey);
        });
        filtered.push(...selectedUnsaved);
        modifications.length = 0;
        modifications.push(...filtered);
      }

      if (modifications.length > 0) {
        packages.push({
          packageId,
          versionId: pkg.versionId,
          modifications,
        });
      }
    }

    Promise.resolve(
      dispatch(priceSimulationActions.submitForApproval({
        packages,
        reviewerIds: selectedReviewerIds,
        publishDate: publishDate || null,
        notes: notes || null,
      }))
    )
      .then(() => {
        dispatch(commonActions.showSuccess(t('priceSimulation.approvalSubmitted')));
        closeModal();
      })
      .catch(() => {
        dispatch(commonActions.showError(t('priceSimulation.approvalSubmitFailed')));
      });
  };

  const getTierCheckState = (tierProducts) => {
    const count = tierProducts.filter((p) => selectedProductKeys.has(p.productKey)).length;
    if (count === 0) return { checked: false, indeterminate: false };
    if (count === tierProducts.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const getPackageCheckState = (tiers) => {
    const allKeys = tiers.flatMap((tierItem) => tierItem.products.map((p) => p.productKey));
    const count = allKeys.filter((k) => selectedProductKeys.has(k)).length;
    if (count === 0) return { checked: false, indeterminate: false };
    if (count === allKeys.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  return (
      <BaseModal size={ModalSize.X_LARGE} isLoading={isSubmitting}>
          <div className="send-for-approval-modal">
              <ModalHeader onclose={closeModal}>
                  <ModalHeaderTitle>{t('priceSimulation.approvalModalTitle')}</ModalHeaderTitle>
                  <p className="approval-modal-subtitle">{t('priceSimulation.approvalModalSubtitle')}</p>
              </ModalHeader>

              <ModalBody>
                  {allDraftProducts.length === 0 ? (
                      <div className="approval-empty-state">
                          <p>{t('priceSimulation.noDraftItems')}</p>
                      </div>
                  ) : (
                      <>
                          {/* Select All / Deselect All toggle */}
                          <div className="approval-select-toggle">
                              <button type="button" className="btn btn-link approval-select-toggle-btn" onClick={handleSelectAll}>
                                  {allSelected ? t('priceSimulation.deselectAll') : t('priceSimulation.selectAll')}
                              </button>
                          </div>

                          {/* Package groups */}
                          <div className="approval-items-list">
                              {draftPackageGroups.map((group) => {
                                  const pkgCheck = getPackageCheckState(group.tiers);
                                  const pkgProductCount = group.tiers.reduce((s, tierItem) => s + tierItem.products.length, 0);
                                  const isPackageExpanded = expandedPackages.has(group.packageId);

                                  return (
                                      <div key={group.packageId} className="approval-package-group">
                                          {/* Level 1: Package — collapsible with bulk checkbox */}
                                          <div className="approval-package-header">
                                              <span className={`expand-chevron ${isPackageExpanded ? 'expanded' : ''}`} onClick={() => handleTogglePackageExpand(group.packageId)}>
                                                  <span className="caret caret-lg" />
                                              </span>
                                              <IndeterminateCheckbox
                                                  checked={pkgCheck.checked}
                                                  indeterminate={pkgCheck.indeterminate}
                                                  onChange={() => handleTogglePackageSelection(group.tiers)}
                                              />
                                              <div className="approval-package-info" onClick={() => handleTogglePackageExpand(group.packageId)}>
                                                  <span className="vismaicon vismaicon-sm vismaicon-box" />
                                                  <span className="approval-package-name">{group.packageName}</span>
                                                  <span className="approval-meta-count">
                                                      {t('priceSimulation.itemCount', { count: pkgProductCount })}
                                                  </span>
                                              </div>
                                          </div>

                                          {/* Level 2: Tiers — accordion headers with bulk checkbox */}
                                          {isPackageExpanded &&
                                              group.tiers.map((tier) => {
                                                  const tierCheck = getTierCheckState(tier.products);
                                                  const isTierExpanded = expandedTiers.has(tier.tierKey);

                                                  return (
                                                      <div key={tier.tierKey} className="approval-tier-section">
                                                          <div className="approval-tier-header">
                                                              <span
                                                                  className={`expand-chevron ${isTierExpanded ? 'expanded' : ''}`}
                                                                  onClick={() => handleToggleTierExpand(tier.tierKey)}
                                                              >
                                                                  <span className="caret caret-lg" />
                                                              </span>
                                                              <IndeterminateCheckbox
                                                                  checked={tierCheck.checked}
                                                                  indeterminate={tierCheck.indeterminate}
                                                                  onChange={() => handleToggleTierSelection(tier.products)}
                                                              />
                                                              <div className="approval-tier-info" onClick={() => handleToggleTierExpand(tier.tierKey)}>
                                                                  <span className="approval-tier-name">{tier.tierName}</span>
                                                                  <span className="approval-meta-count">
                                                                      {t('priceSimulation.productCount', { count: tier.products.length })}
                                                                  </span>
                                                              </div>
                                                          </div>

                                                          {/* Level 3: Products — checkboxes here */}
                                                          {isTierExpanded && (
                                                              <div className="approval-product-list">
                                                                  {tier.products.map((product) => (
                                                                      <label key={product.productKey} className="approval-product-row">
                                                                          <input
                                                                              type="checkbox"
                                                                              checked={selectedProductKeys.has(product.productKey)}
                                                                              onChange={() => handleToggleProduct(product.productKey)}
                                                                          />
                                                                          <div className="approval-product-info">
                                                                              <div className="approval-product-line1">
                                                                                  <span className="approval-product-name">
                                                                                      {product.productName} ({product.planIdentifier})
                                                                                  </span>
                                                                                  {product.groupName && (
                                                                                      <>
                                                                                          <span className="approval-product-group-separator">&bull;</span>
                                                                                          <span className="approval-product-group">{product.groupName}</span>
                                                                                      </>
                                                                                  )}
                                                                              </div>
                                                                              <div className="approval-product-price">
                                                                                  {formatPrice(product.currentPrice)} SEK &rarr; {formatPrice(product.newPrice)} SEK
                                                                              </div>
                                                                          </div>
                                                                          {product.adjustmentPct != null && product.adjustmentPct !== 0 && (
                                                                              <span
                                                                                  className={`approval-adjustment-pct ${product.adjustmentPct > 0 ? 'approval-pct-increase' : 'approval-pct-decrease'}`}
                                                                              >
                                                                                  {product.adjustmentPct > 0 ? '+' : ''}
                                                                                  {product.adjustmentPct.toFixed(2)}%
                                                                              </span>
                                                                          )}
                                                                      </label>
                                                                  ))}
                                                              </div>
                                                          )}
                                                      </div>
                                                  );
                                              })}
                                      </div>
                                  );
                              })}
                          </div>

                          {/* Summary bar */}
                          <div className="approval-summary-bar">
                              <div className="approval-summary-stat">
                                  <span className="approval-summary-label">{t('priceSimulation.productsSelected')}</span>
                                  <span className="approval-summary-value">{summaryStats.totalProducts}</span>
                              </div>
                              <div className="approval-summary-stat">
                                  <span className="approval-summary-label">{t('priceSimulation.expectedRevenueImpact')}</span>
                                  <span className={`approval-summary-value ${summaryStats.totalRevenueImpact >= 0 ? 'approval-summary-positive' : 'approval-summary-negative'}`}>
                                      {summaryStats.totalRevenueImpact >= 0 ? '+' : ''}
                                      {formatPrice(summaryStats.totalRevenueImpact)} SEK
                                  </span>
                              </div>
                          </div>
                      </>
                  )}

                  {/* Reviewers */}
                  <div className="approval-field">
                      <label className="approval-field-label">{t('priceSimulation.selectReviewers')}</label>
                      <ComboBox
                          options={reviewers}
                          value={selectedReviewerIds}
                          onChange={(_values, options) => setSelectedReviewerIds(options.map((o) => o.id))}
                          isMulti={true}
                          valueKey="id"
                          labelKey="name"
                          placeholder={t('priceSimulation.reviewersPlaceholder')}
                          strategy="dialog"
                      />
                  </div>

                  {/* Publishing Date */}
                  <div className="approval-field">
                      <DateTimePicker
                          label={t('priceSimulation.publishingDate')}
                          value={publishDate}
                          onChange={(value) => setPublishDate(value)}
                          showTime={false}
                          placeholderText={t('priceSimulation.publishingDatePlaceholder')}
                      />
                  </div>

                  {/* Notes */}
                  <div className="approval-field">
                      <label className="approval-field-label">{t('priceSimulation.additionalNotes')}</label>
                      <textarea
                          className="form-control approval-notes-textarea"
                          rows={4}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder={t('priceSimulation.notesPlaceholder')}
                      />
                  </div>
              </ModalBody>

              <ModalFooter>
                  <div className="w-100 d-flex justify-content-end">
                      <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
                          {t('priceSimulation.sendXForApproval', { count: selectedCount })}
                      </button>
                      <button type="button" className="btn" onClick={closeModal}>
                          {t('baseModal.cancel')}
                      </button>
                  </div>
              </ModalFooter>
          </div>
      </BaseModal>
  );
};

export default SendPriceForApprovalModalBeta;
